/**
 * AssignmentsTable.js
 *
 * Default view for the BOT Clearance page.
 *
 * @component
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Tag,
  Button,
  Input,
  Typography,
  Row,
  Col,
  message,
  Badge,
  Spin,
} from "antd";
import {
  SearchOutlined,
  ArrowRightOutlined,
  LoadingOutlined,
  UserOutlined,
} from "@ant-design/icons";
import Utilities from "../../../../dependencies/custom/react-utilities";
import domainSettings from "../../../../dependencies/custom/settings";

const { Text } = Typography;

/**
 * Returns color and label for status tag
 */
const getStatusConfig = (status) => {
  const configs = {
    "Pending Selection": { color: "orange", label: "Pending Selection" },
    "Items Selected": { color: "blue", label: "Items Selected" },
    "Pending Payment": { color: "gold", label: "Awaiting Payment" },
    "Partially Paid": { color: "cyan", label: "Partially Paid" },
    Paid: { color: "green", label: "Paid" },
    "Ready for Pickup": { color: "purple", label: "Ready for Pickup" },
    "Partially Collected": { color: "geekblue", label: "Partially Collected" },
    Collected: { color: "blue", label: "Collected" },
    Cleared: { color: "success", label: "Cleared" },
  };
  return configs[status] || { color: "default", label: status };
};

/**
 * AssignmentsTable Component
 *
 * @param {function} onSelectAssignment - Callback when user clicks to continue clearance
 */
const AssignmentsTable = ({ onSelectAssignment }) => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState({});
  const [studentAssignments, setStudentAssignments] = useState({});

  /**
   * Fetch admitted students for the current user
   */
  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${domainSettings.urls.enrollment.admission}?status=ADMITTED`;
      const response = await Utilities.fetchWithAuth(
        "GET",
        url,
        null,
        navigate,
      );

      if (response.ok) {
        const admittedStudents = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.results)
            ? response.data.results
            : [];
        const validStudents = admittedStudents.filter(
          (admission) => admission.student && admission.student.spcCode,
        );
        setStudents(validStudents);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      message.error("Failed to load students");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  /**
   * Fetch assignments for a specific student when row is expanded
   */
  const fetchAssignmentsForStudent = async (spc) => {
    if (studentAssignments[spc]) return;

    setLoadingAssignments((prev) => ({ ...prev, [spc]: true }));
    try {
      const url = `${domainSettings.urls.bot.clearanceAssignments}?student=${encodeURIComponent(spc)}`;
      const response = await Utilities.fetchWithAuth(
        "GET",
        url,
        null,
        navigate,
      );

      if (response.ok) {
        const assignments = response.data.results || response.data || [];
        setStudentAssignments((prev) => ({
          ...prev,
          [spc]: Array.isArray(assignments) ? assignments : [],
        }));
      } else {
        setStudentAssignments((prev) => ({ ...prev, [spc]: [] }));
      }
    } catch (error) {
      console.error(`Error fetching assignments for ${spc}:`, error);
      setStudentAssignments((prev) => ({ ...prev, [spc]: [] }));
    } finally {
      setLoadingAssignments((prev) => ({ ...prev, [spc]: false }));
    }
  };

  /**
   * Handle row expansion
   */
  const handleExpand = (expanded, record) => {
    const spc = record.student.spcCode;
    if (expanded) {
      setExpandedRowKeys([spc]);
      fetchAssignmentsForStudent(spc);
    } else {
      setExpandedRowKeys([]);
    }
  };

  /**
   * Handle assignment selection
   */
  const handleSelectAssignment = (assignment, student) => {
    onSelectAssignment({
      id: assignment.id,
      student_name:
        assignment.student_name || `${student.firstName} ${student.lastName}`,
      student_spc: student.spcCode,
      botpc: assignment.botpc,
      template_name: assignment.template_name,
      status: assignment.status,
      total_amount: assignment.total_amount,
      balance: assignment.balance,
      term_name: assignment.term_name,
      acadyr: assignment.acadyr,
      ...assignment,
    });
  };

  /**
   * Filter students based on search text
   */
  const filteredStudents = students.filter((record) => {
    const student = record.student;
    if (!searchText) return true;

    const search = searchText.toLowerCase();
    const fullName =
      `${student.firstName || ""} ${student.middleName || ""} ${student.lastName || ""}`.toLowerCase();
    return (
      fullName.includes(search) ||
      student.spcCode?.toLowerCase().includes(search) ||
      student.className?.toLowerCase().includes(search)
    );
  });

  /**
   * Columns for the expanded assignments table
   */
  const assignmentColumns = (student) => [
    {
      title: "BOTPC",
      key: "botpc",
      render: (_, assignment) => (
        <Text code style={{ fontWeight: 500 }}>
          {assignment.botpc}
        </Text>
      ),
    },
    {
      title: "Term",
      key: "term",
      render: (_, assignment) => (
        <div>
          <div>{assignment.term_name}</div>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {assignment.acadyr}
          </Text>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const config = getStatusConfig(status);
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: "Amount",
      key: "amount",
      render: (_, assignment) => (
        <div>
          <Text strong>
            UGX {parseFloat(assignment.total_amount || 0).toLocaleString()}
          </Text>
          {parseFloat(assignment.balance || 0) > 0 && (
            <div>
              <Text type="danger" style={{ fontSize: "12px" }}>
                Balance: UGX{" "}
                {parseFloat(assignment.balance || 0).toLocaleString()}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, assignment) => {
        const isCompleted = [
          "Paid",
          "Ready for Pickup",
          "Partially Collected",
          "Collected",
          "Cleared",
        ].includes(assignment.status);
        return (
          <Button
            type={isCompleted ? "default" : "primary"}
            icon={<ArrowRightOutlined />}
            onClick={() => handleSelectAssignment(assignment, student)}
          >
            {isCompleted ? "View" : "Continue"}
          </Button>
        );
      },
    },
  ];

  /**
   * Expanded row render - shows assignments for the student
   */
  const expandedRowRender = (record) => {
    const spc = record.student.spcCode;
    const assignments = studentAssignments[spc] || [];
    const isLoading = loadingAssignments[spc];

    if (isLoading) {
      return (
        <div style={{ padding: "20px", textAlign: "center" }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
          <div style={{ marginTop: 8 }}>Loading assignments...</div>
        </div>
      );
    }

    if (assignments.length === 0) {
      return (
        <div style={{ padding: "20px", textAlign: "center" }}>
          <Text type="secondary">
            No BOT clearance assignments found for this student.
          </Text>
        </div>
      );
    }

    return (
      <div style={{ padding: "8px 0" }}>
        <Table
          columns={assignmentColumns(record.student)}
          dataSource={assignments}
          rowKey="id"
          pagination={false}
          size="small"
          style={{ background: "#fafafa", borderRadius: 8 }}
        />
      </div>
    );
  };

  /**
   * Main table columns - Student list
   */
  const columns = [
    {
      title: "Student",
      key: "student",
      render: (_, record) => {
        const student = record.student;
        const fullName = [
          student.firstName,
          student.middleName,
          student.lastName,
        ]
          .filter(Boolean)
          .join(" ");

        return (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "#1890ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontWeight: 500,
              }}
            >
              {student.firstName?.[0]}
              {student.lastName?.[0]}
            </div>
            <div>
              <div style={{ fontWeight: 500 }}>{fullName || "N/A"}</div>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                {student.spcCode}
              </Text>
            </div>
          </div>
        );
      },
    },
    {
      title: "Class",
      key: "class",
      render: (_, record) => record.student?.className || "N/A",
    },
    {
      title: "Assignments",
      key: "assignments",
      align: "center",
      render: (_, record) => {
        const spc = record.student.spcCode;
        const assignments = studentAssignments[spc];
        const isExpanded = expandedRowKeys.includes(spc);

        if (!isExpanded || !assignments) {
          return <Text type="secondary">Click to view</Text>;
        }

        const pendingCount = assignments.filter(
          (a) =>
            ![
              "Paid",
              "Ready for Pickup",
              "Partially Collected",
              "Collected",
              "Cleared",
            ].includes(a.status),
        ).length;

        return (
          <div>
            <Badge
              count={assignments.length}
              style={{ backgroundColor: "#52c41a" }}
              title="Total assignments"
            />
            {pendingCount > 0 && (
              <Badge
                count={pendingCount}
                style={{ backgroundColor: "#faad14", marginLeft: 8 }}
                title="Pending"
              />
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      {/* Search Section */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={8}>
          <Input
            placeholder="Search by student name, SPC, or class..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            size="large"
          />
        </Col>
        <Col xs={24} sm={12} md={16} style={{ textAlign: "right" }}>
          <Text type="secondary">
            <UserOutlined style={{ marginRight: 4 }} />
            {filteredStudents.length} student
            {filteredStudents.length !== 1 ? "s" : ""}
          </Text>
        </Col>
      </Row>

      {/* Students Table with Expandable Rows */}
      <Table
        columns={columns}
        dataSource={filteredStudents}
        rowKey={(record) => record.student.spcCode}
        loading={loading}
        expandable={{
          expandedRowRender,
          expandedRowKeys,
          onExpand: handleExpand,
          expandRowByClick: true,
        }}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} students`,
        }}
      />
    </div>
  );
};

export default AssignmentsTable;
